import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useArs } from "./components";
import { onOpenMarketCart, openMarketCart, useMarketCart } from "./cart";

/**
 * The market cart (while the store sells nothing of its own, the header's cart): what the buyer is
 * gathering, grouped by seller. Each seller is its own order and gets paid directly, so each group
 * has its own "Comprar a @vendedor".
 */
export function MarketCartDrawer() {
  const { t } = useI18n();
  const ars = useArs();
  const { carts, setQuantity, remove } = useMarketCart();
  const [isOpen, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  const groups = Object.entries(carts).filter(([, lines]) => lines.length > 0);
  const count = groups.reduce((n, [, lines]) => n + lines.reduce((m, l) => m + l.quantity, 0), 0);

  useEffect(() => onOpenMarketCart(() => setOpen(true)), []);
  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div className="drawer-root">
      <div className="drawer-backdrop" onClick={close} aria-hidden="true" />
      <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="mcart-title" tabIndex={-1} ref={panelRef}>
        <div className="drawer-head">
          <h2 id="mcart-title">
            {t("mcart.title")} <small>({t("cart.items", { count })})</small>
          </h2>
          <button type="button" className="icon-btn" aria-label={t("nav.close")} onClick={close}>
            ✕
          </button>
        </div>
        {groups.length === 0 ? (
          <div className="drawer-empty">
            <p>{t("mcart.empty")}</p>
            <Link to="/mercado" className="btn btn-primary" onClick={close}>
              {t("mcart.browse")}
            </Link>
          </div>
        ) : (
          <div className="drawer-scroll">
            {/* No total across sellers: it isn't an amount anyone gets paid, and it could end up in one transfer. */}
            {groups.length > 1 && <p className="notice notice-warn small">{t("mcart.perSeller", { count: groups.length })}</p>}
            {groups.map(([handle, lines], i) => (
              <section key={handle} className="mcart-group" aria-label={`@${handle}`}>
                <h3 className="small-title">
                  {groups.length > 1 && <span className="muted">{t("mcart.orderOf", { n: i + 1, count: groups.length })} · </span>}
                  <Link to={`/vendedores/${handle}`} onClick={close}>
                    @{handle}
                  </Link>
                </h3>
                <ul className="cart-lines">
                  {lines.map((l) => (
                    <li key={l.listingId} className="cart-line">
                      {l.photo ? <img src={imageUrl(l.photo)} alt="" width={64} height={64} /> : <span className="cart-line-noimg" aria-hidden="true" />}
                      <div className="cart-line-info">
                        <Link to={`/mercado/${l.listingId}`} onClick={close}>
                          {l.name}
                        </Link>
                        <span className="cart-line-price">{ars(l.priceArs)}</span>
                        <div className="cart-line-actions">
                          {l.max > 1 && !l.offerId && (
                            <input
                              type="number"
                              min={1}
                              max={l.max}
                              value={l.quantity}
                              aria-label={`${t("product.quantity")}: ${l.name}`}
                              onChange={(e) => setQuantity(handle, l.listingId, Number(e.target.value) || 1)}
                              className="qty-input"
                            />
                          )}
                          <button type="button" className="btn btn-link" onClick={() => remove(handle, l.listingId)}>
                            {t("cart.remove")}
                          </button>
                        </div>
                      </div>
                      <strong className="cart-line-total">{ars(l.priceArs * l.quantity)}</strong>
                    </li>
                  ))}
                </ul>
                <div className="subtotal">
                  <span>{t("mcart.cardsOf", { handle })}</span>
                  <strong>{ars(lines.reduce((n, l) => n + l.priceArs * l.quantity, 0))}</strong>
                </div>
                <Link to={`/mercado/comprar/${handle}`} className="btn btn-primary btn-block" onClick={close}>
                  {t("mcart.buyFrom", { handle })}
                </Link>
              </section>
            ))}
            <p className="muted small">{t("mcart.exactLater")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** The header's cart button for the market cart: the units gathered across sellers. */
export function MarketCartButton() {
  const { t } = useI18n();
  const { carts } = useMarketCart();
  const count = Object.values(carts).reduce((n, lines) => n + lines.reduce((m, l) => m + l.quantity, 0), 0);
  return (
    <button type="button" className="icon-btn cart-btn" onClick={openMarketCart} aria-label={t("cart.open", { count })}>
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.6a2 2 0 0 0 2-1.5L21 8H6.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <circle cx="10" cy="20" r="1.5" fill="currentColor" />
        <circle cx="17" cy="20" r="1.5" fill="currentColor" />
      </svg>
      {count > 0 && <span className="badge">{count}</span>}
    </button>
  );
}
